import type { ExpressionSpecification, Map as MaplibreMap } from "maplibre-gl";

/** Inline copy of "public/Icons/Map-pin.svg" — used if fetch fails (offline / bad path). */
// eslint-disable-next-line quotes -- SVG markup; double quotes inside attributes
const FALLBACK_MAP_PIN_SVG = `<svg width="78" height="114" viewBox="0 0 78 114" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M38.627 0C59.9601 0 77.2539 17.2938 77.2539 38.627C77.2538 51.7359 70.722 63.3167 60.7373 70.2998L38.627 113.774L16.5146 70.2988C6.53099 63.3156 6.96542e-05 51.735 0 38.627C0 17.2938 17.2938 5.44404e-05 38.627 0Z" fill="#1178BB"/><circle cx="39.3295" cy="39.3295" r="22.474" fill="white"/></svg>`;

/** Inline copy of "public/Icons/map-pin-hover.svg". */
// eslint-disable-next-line quotes -- SVG markup; double quotes inside attributes
const FALLBACK_MAP_PIN_HOVER_SVG = `<svg width="126" height="185" viewBox="0 0 126 185" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M62.7324 0C97.3782 0.000251648 125.464 28.0866 125.464 62.7324C125.464 84.0234 114.856 102.835 98.6387 114.176L62.7324 184.775L26.8242 114.176C10.6071 102.835 0 84.0231 0 62.7324C3.62744e-06 28.0864 28.0864 0 62.7324 0Z" fill="#23538A"/><circle cx="63.8727" cy="63.8717" r="36.4987" fill="white"/></svg>`;

/** Shared pin asset for all MapLibre maps. */
export function getFollowersMapPinIconUrl(): string {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/Icons/Map-pin.svg`;
}

export function getFollowersMapPinHoverIconUrl(): string {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/Icons/map-pin-hover.svg`;
}

function toAbsoluteFetchUrl(path: string): string {
  if (typeof window === "undefined") {
    return path;
  }
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${normalized}`;
}

/** Absolute URL for `fetch` (avoids relative-path issues in some environments). */
export function getFollowersMapPinAbsoluteUrl(): string {
  return toAbsoluteFetchUrl(getFollowersMapPinIconUrl());
}

export function getFollowersMapPinHoverAbsoluteUrl(): string {
  return toAbsoluteFetchUrl(getFollowersMapPinHoverIconUrl());
}

export const FOLLOWERS_MAP_PIN_IMAGE_ID = "followers-blue-pin";

/** Raster registered from `public/Icons/map-pin-hover.svg` — focused / hovered pin. */
export const FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID = "followers-blue-pin-hover";

export const FOLLOWERS_MAP_PIN_CLICK_ZOOM = 12;

/** Fixed size used across all maps so pins stay visually consistent. */
export const STANDARD_MAP_PIN_ICON_SIZE = 0.17;

/** Kept as a shared helper so existing maps can use one uniform pin size. */
export function mapPinWeightedIconSize(
  _maxValue: number,
  _valueProperty: string,
): ExpressionSpecification {
  return ["literal", STANDARD_MAP_PIN_ICON_SIZE];
}

export function followerWeightedIconSize(_maxF: number): ExpressionSpecification {
  return ["literal", STANDARD_MAP_PIN_ICON_SIZE];
}

/**
 * MapLibre often renders **no** symbols when `layout["icon-image"]` depends on `feature-state`.
 * Use two symbol layers instead: constant `icon-image` on each, and drive visibility with
 * `paint["icon-opacity"]` (feature-state is reliable in paint).
 */

/** Default pin layer: dimmed when idle; hidden when hover/selected so the focus layer shows. */
export const MAP_PIN_BASE_ICON_OPACITY: ExpressionSpecification = [
  "case",
  ["boolean", ["feature-state", "hover"], false],
  0,
  ["case", ["boolean", ["feature-state", "selected"], false], 0, 0.94],
];

/** Same as {@link MAP_PIN_BASE_ICON_OPACITY} but idle opacity is 1 (Web Analytics world map). */
export const MAP_PIN_BASE_ICON_OPACITY_SOLID: ExpressionSpecification = [
  "case",
  ["boolean", ["feature-state", "hover"], false],
  0,
  ["case", ["boolean", ["feature-state", "selected"], false], 0, 1],
];

/** Hover pin layer: visible only for hover or selected (Shopify). */
export const MAP_PIN_FOCUS_ICON_OPACITY: ExpressionSpecification = [
  "case",
  ["boolean", ["feature-state", "hover"], false],
  1,
  ["case", ["boolean", ["feature-state", "selected"], false], 1, 0],
];

async function rasterizeSvgUrlToImage(
  absoluteUrl: string,
  fallbackSvg: string,
): Promise<ImageBitmap | HTMLImageElement> {
  let svgText: string;
  try {
    const res = await fetch(absoluteUrl, { cache: "force-cache" });
    if (!res.ok) {
      throw new Error(String(res.status));
    }
    svgText = await res.text();
  } catch {
    svgText = fallbackSvg;
  }

  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
  const htmlImg = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("map pin SVG decode failed"));
    img.src = dataUrl;
  });

  const w = htmlImg.naturalWidth || 100;
  const h = htmlImg.naturalHeight || 134;
  const dpr = typeof window !== "undefined" ? Math.min(2, window.devicePixelRatio || 2) : 2;
  const cw = Math.max(64, Math.round(w * dpr));
  const ch = Math.max(64, Math.round(h * dpr));

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return htmlImg;
  }
  ctx.clearRect(0, 0, cw, ch);
  ctx.drawImage(htmlImg, 0, 0, cw, ch);

  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(canvas);
    } catch {
      return htmlImg;
    }
  }
  return htmlImg;
}

export async function loadFollowersMapPinImage(): Promise<ImageBitmap | HTMLImageElement> {
  return rasterizeSvgUrlToImage(getFollowersMapPinAbsoluteUrl(), FALLBACK_MAP_PIN_SVG);
}

export async function loadFollowersMapPinHoverImage(): Promise<ImageBitmap | HTMLImageElement> {
  return rasterizeSvgUrlToImage(getFollowersMapPinHoverAbsoluteUrl(), FALLBACK_MAP_PIN_HOVER_SVG);
}

/** Options for `map.addImage` when using rasterized pins from {@link loadFollowersMapPinImage}. */
export const FOLLOWERS_MAP_PIN_ADD_IMAGE_OPTIONS: { pixelRatio: number } = { pixelRatio: 1 };

let cachedRasterPin: ImageBitmap | HTMLImageElement | null = null;
let rasterPinInFlight: Promise<ImageBitmap | HTMLImageElement> | null = null;

let cachedRasterPinHover: ImageBitmap | HTMLImageElement | null = null;
let rasterPinHoverInFlight: Promise<ImageBitmap | HTMLImageElement> | null = null;

/** Single decode/rasterize of the SVG pin for the whole app (fetch + canvas once). */
async function getRasterMapPinImageCached(): Promise<ImageBitmap | HTMLImageElement> {
  if (cachedRasterPin) {
    return cachedRasterPin;
  }
  if (!rasterPinInFlight) {
    rasterPinInFlight = loadFollowersMapPinImage()
      .then(img => {
        cachedRasterPin = img;
        rasterPinInFlight = null;
        return img;
      })
      .catch(err => {
        rasterPinInFlight = null;
        throw err;
      });
  }
  return rasterPinInFlight;
}

async function getRasterMapPinHoverImageCached(): Promise<ImageBitmap | HTMLImageElement> {
  if (cachedRasterPinHover) {
    return cachedRasterPinHover;
  }
  if (!rasterPinHoverInFlight) {
    rasterPinHoverInFlight = loadFollowersMapPinHoverImage()
      .then(img => {
        cachedRasterPinHover = img;
        rasterPinHoverInFlight = null;
        return img;
      })
      .catch(err => {
        rasterPinHoverInFlight = null;
        throw err;
      });
  }
  return rasterPinHoverInFlight;
}

/**
 * MapLibre may transfer or invalidate an `ImageBitmap` after `addImage`; multiple maps
 * on one page each need their own copy for stable rendering.
 */
async function copyImageForMapAdd(img: ImageBitmap | HTMLImageElement): Promise<ImageBitmap | HTMLImageElement> {
  if (img instanceof ImageBitmap && typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(img);
    } catch {
      return img;
    }
  }
  return img;
}

/**
 * Ensures default and hover pins are registered on this map. Reuses rasterized assets
 * across MapLibre instances so opening multiple dashboards does not repeat fetch/decode work.
 */
export async function ensureStandardMapPinOnMap(map: MaplibreMap): Promise<boolean> {
  let defaultRaster: ImageBitmap | HTMLImageElement;
  try {
    defaultRaster = await getRasterMapPinImageCached();
  } catch {
    return false;
  }

  try {
    if (!map.hasImage(FOLLOWERS_MAP_PIN_IMAGE_ID)) {
      map.addImage(
        FOLLOWERS_MAP_PIN_IMAGE_ID,
        await copyImageForMapAdd(defaultRaster),
        FOLLOWERS_MAP_PIN_ADD_IMAGE_OPTIONS,
      );
    }
  } catch {
    return false;
  }

  let hoverRaster: ImageBitmap | HTMLImageElement;
  try {
    hoverRaster = await getRasterMapPinHoverImageCached();
  } catch {
    hoverRaster = defaultRaster;
  }

  try {
    if (!map.hasImage(FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID)) {
      map.addImage(
        FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID,
        await copyImageForMapAdd(hoverRaster),
        FOLLOWERS_MAP_PIN_ADD_IMAGE_OPTIONS,
      );
    }
  } catch {
    try {
      if (!map.hasImage(FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID)) {
        map.addImage(
          FOLLOWERS_MAP_PIN_HOVER_IMAGE_ID,
          await copyImageForMapAdd(defaultRaster),
          FOLLOWERS_MAP_PIN_ADD_IMAGE_OPTIONS,
        );
      }
    } catch {
      return false;
    }
  }

  return true;
}
